import { exportChartPng } from './chartExport';

const slug = value => String(value || '')
  .replace(/[^\w-]+/g, '_')
  .replace(/^_+|_+$/g, '');

// Lap time in seconds from the lap label ("1:23.456" / "83.456"), or null when
// the label is a bare "L<n>" placeholder.
function lapSeconds(item) {
  if (!item.lapLabel || !item.lapLabel.includes('.')) return null;
  const parts = item.lapLabel.split(':');
  const seconds = parts.length === 2
    ? parseFloat(parts[0]) * 60 + parseFloat(parts[1])
    : parseFloat(item.lapLabel);
  return Number.isFinite(seconds) ? seconds : null;
}

/**
 * PNG export shared by the channel-trace renderers (single and multi-signal).
 * Builds the title band from the track / vehicle / date when they are uniform
 * across the plotted sessions, moves them into the legend rows when they
 * differ, and appends each lap's gap to the fastest plotted lap.
 *
 * `series` items carry `source` ('primary' or a compare-driver slot),
 * `lapNumber`, `lapLabel`, `color` and optionally `signalName` (included in the
 * legend row when present, so a multi-channel legend says which channel each
 * colour belongs to).
 */
export function exportTracePanelPng({
  svgs,
  themeFrom,
  series,
  fallbackTitle,
  subtitle,
  slugBits = [],
  plotTop,
  plotBottom,
  trackName = '',
  sessionDate = '',
  sessionVehicle = '',
  driverName1 = 'Driver 1',
  exportCompareDrivers = [],
}) {
  const svg = svgs[0];
  if (!svg || !series.length) return Promise.resolve();

  const driverFor = item => {
    if (item.source === 'primary') {
      return { name: driverName1, date: sessionDate, vehicle: sessionVehicle };
    }
    const driver = exportCompareDrivers.find(candidate => candidate.slot === item.source);
    return {
      name: driver?.name || 'Driver',
      date: driver?.session?.meta?.date || '',
      vehicle: driver?.session?.meta?.vehicle || '',
    };
  };
  const activeDrivers = series.map(driverFor);
  const dates = new Set(activeDrivers.map(driver => driver.date).filter(Boolean));
  const vehicles = new Set(activeDrivers.map(driver => driver.vehicle).filter(Boolean));
  const mixedDates = dates.size > 1;
  const mixedVehicles = vehicles.size > 1;
  const uniformDate = mixedDates ? '' : [...dates][0] || '';
  const uniformVehicle = mixedVehicles ? '' : [...vehicles][0] || '';

  const bestSeconds = Math.min(...series.map(item => lapSeconds(item) ?? Infinity));
  const legend = series.map(item => {
    const driver = driverFor(item);
    const lapBit = item.lapLabel === `L${item.lapNumber}`
      ? item.lapLabel
      : `L${item.lapNumber} · ${item.lapLabel}`;
    const seconds = lapSeconds(item);
    const deltaBit = seconds != null && Number.isFinite(bestSeconds) && seconds > bestSeconds
      ? ` (+${(seconds - bestSeconds).toFixed(3)})`
      : '';
    const signalBit = item.signalName ? `${item.signalName} · ` : '';
    return {
      color: item.color,
      label: `${driver.name} — ${signalBit}${lapBit}${deltaBit}${
        mixedVehicles && driver.vehicle ? ` · ${driver.vehicle}` : ''}${
        mixedDates && driver.date ? ` · ${driver.date}` : ''}`,
    };
  });

  const host = window.location.hostname;
  return exportChartPng({
    svg,
    svgs,
    themeFrom,
    title: [trackName, uniformVehicle, uniformDate].filter(Boolean).join(' · ')
      || fallbackTitle || 'Channel Trace',
    subtitle,
    legend,
    plotTop,
    plotBottom,
    watermark: /^(localhost|127\.|\d+\.\d+\.)/.test(host) ? 'TrackDay Analysis' : host,
    filename: `${[
      slug(trackName) || 'track',
      slug(uniformDate),
      ...slugBits.map(slug),
    ].filter(Boolean).join('_')}.png`,
  });
}
